import {
  InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import moment from 'moment-timezone'
import { useDispatch } from 'react-redux'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import {
  CreditCardListModel,
  CreditCardListResponse,
} from 'src/models/creditCard.models'
import {
  CreditCardPermissionsModel,
  CreditCardPermissionsResponse,
} from 'src/models/creditCardPermissions.models'
import {
  CreditCardsResponse,
  getCreditCard,
  getCreditCardPermissions,
  getCreditCards,
  getCreditCardsPaginated,
} from 'src/queries/creditCard.network'
import { QueryError } from 'src/queries/network.utils'
import { creditCardsQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getNextExtendPageParam,
} from 'src/queries/queries.utils'
import {
  GET_CREDIT_CARD_INIT,
  GET_CREDIT_CARD_PERMISSIONS_FAILURE,
  GET_CREDIT_CARD_PERMISSIONS_INIT,
  GET_CREDIT_CARD_PERMISSIONS_SUCCESS,
  GET_CREDIT_CARD_SUCCESS,
  GET_CREDIT_CARDS_FAILURE,
  GET_CREDIT_CARDS_INIT,
  GET_CREDIT_CARDS_SUCCESS,
  getCreditCardsStatisticsActiveBudgetCount,
  getCreditCardsStatisticsActiveVcnCount,
  getCreditCardsStatisticsBudgetCount,
  getCreditCardsStatisticsVcnCount,
} from 'src/redux/actions/data/creditCards.actions'
import { GET_VIRTUAL_CARD_FAILURE } from 'src/redux/actions/data/virtualCards.actions'
import { RootState } from 'src/redux/createRootReducer'
import { GetCreditCardsRequest } from 'src/requests/getCreditCards.request'

// Re-exported so the pre-existing importers of this type
// (creditCards/network/virtualCardFormCreditCards.network.ts,
// creditCards/domain/useVirtualCardFormCreditCards.ts and its test) keep
// resolving `CreditCardsResponse` from 'src/queries/creditCard.queries'; the
// type itself now lives with the network functions in creditCard.network.ts.
export type { CreditCardsResponse } from 'src/queries/creditCard.network'

const cacheTimestamp = () => moment().format('dddd, Do MMM YYYY, h:mm:ss A')

// The credit-card query factories dispatch redux side effects from inside the
// queryFn (React Query v5 dropped useQuery onSuccess/onError), reproducing the
// exact INIT / SUCCESS / FAILURE cadence and payloads the legacy useExtendQuery
// wrappers ran. `dispatch` is injected into the factory (the receiptAttachments
// precedent) so the factories stay hook-free and unit-testable.
type CreditCardQueriesDispatch = ThunkDispatch<RootState, void, AnyAction>

// getResponseData throws a normalized QueryError; the legacy wrappers handed the
// redux FAILURE reducers the raw AxiosError (their onError received the
// un-normalized error). Recover that raw error from the non-enumerable
// `originalError` so the reducers see byte-identical payloads post-migration.
const toReduxErrorPayload = (error: unknown): unknown =>
  (error as QueryError | undefined)?.originalError ?? error

export interface CreditCardsDataModel {
  creditCards: CreditCardListModel[]
  page: number
  totalItems: number
}

const selectCreditCardListModel = (
  response: InfiniteData<CreditCardsResponse, number>,
): CreditCardsDataModel => ({
  creditCards: response?.pages.flatMap((page) =>
    page.creditCards.map((creditCard) => new CreditCardListModel(creditCard)),
  ),
  page: response.pageParams.length - 1,
  totalItems: response?.pages[0]?.pagination.totalItems,
})

const selectCreditCardDetailModel = (data: {
  creditCard: CreditCardListResponse
}): CreditCardListModel => new CreditCardListModel(data.creditCard)

const selectCreditCardPermissionsModel = (
  data: CreditCardPermissionsResponse,
): CreditCardPermissionsModel => new CreditCardPermissionsModel(data)

export const creditCardQueries = {
  detail: (
    dispatch: CreditCardQueriesDispatch,
    creditCardId: string | undefined,
  ) =>
    queryOptions({
      queryKey: creditCardsQueryKeys.detail(creditCardId),
      queryFn: async () => {
        if (!creditCardId) {
          throw new Error('creditCardQueries.detail: creditCardId is required')
        }
        dispatch({ type: GET_CREDIT_CARD_INIT })
        try {
          const data = await getCreditCard(creditCardId)
          // TODO: remove when everything is moved to react query
          dispatch({ type: GET_CREDIT_CARD_SUCCESS, payload: data })
          dispatch(
            getCreditCardsStatisticsActiveBudgetCount(
              [data.creditCard.id],
              cacheTimestamp(),
            ),
          )
          dispatch(
            getCreditCardsStatisticsBudgetCount(
              [data.creditCard.id],
              cacheTimestamp(),
            ),
          )
          dispatch(getCreditCardsStatisticsActiveVcnCount([data.creditCard.id]))
          dispatch(getCreditCardsStatisticsVcnCount([data.creditCard.id]))
          return data
        } catch (error) {
          dispatch({
            type: GET_VIRTUAL_CARD_FAILURE,
            payload: toReduxErrorPayload(error),
          })
          throw error
        }
      },
      enabled: creditCardId !== undefined,
      select: selectCreditCardDetailModel,
    }),

  permissions: (
    dispatch: CreditCardQueriesDispatch,
    creditCardId: string | undefined,
  ) =>
    queryOptions({
      queryKey: creditCardsQueryKeys.permissions(creditCardId),
      queryFn: async () => {
        if (!creditCardId) {
          throw new Error(
            'creditCardQueries.permissions: creditCardId is required',
          )
        }
        dispatch({ type: GET_CREDIT_CARD_PERMISSIONS_INIT })
        try {
          const data = await getCreditCardPermissions(creditCardId)
          dispatch({ type: GET_CREDIT_CARD_PERMISSIONS_SUCCESS, payload: data })
          return data
        } catch (error) {
          dispatch({
            type: GET_CREDIT_CARD_PERMISSIONS_FAILURE,
            payload: toReduxErrorPayload(error),
          })
          throw error
        }
      },
      enabled: creditCardId !== undefined,
      select: selectCreditCardPermissionsModel,
    }),

  list: (
    dispatch: CreditCardQueriesDispatch,
    filters: GetCreditCardsRequest | undefined,
    additionalKey?: string,
  ) =>
    infiniteQueryOptions({
      queryKey: creditCardsQueryKeys.list(filters, additionalKey),
      queryFn: async ({ pageParam }) => {
        if (!filters) {
          throw new Error('creditCardQueries.list: filters is required')
        }
        dispatch({ type: GET_CREDIT_CARDS_INIT })
        try {
          const data = await getCreditCards(filters, pageParam)
          // TODO: lots of side-effects in creditCards.actions.tsx that need to be implemented for a full refactor
          dispatch({
            type: GET_CREDIT_CARDS_SUCCESS,
            payload: { ...data, queryParameters: filters },
          })
          return data
        } catch (error) {
          dispatch({
            type: GET_CREDIT_CARDS_FAILURE,
            payload: {
              ...(toReduxErrorPayload(error) as object),
              queryParameters: filters,
            },
          })
          throw error
        }
      },
      enabled: filters !== undefined,
      initialPageParam: 0,
      getNextPageParam: (lastPage) =>
        getNextExtendPageParam(lastPage.pagination),
      select: selectCreditCardListModel,
      // re-fetches happen in place without resetting back to an empty state, less flashing of content
      placeholderData: keepPreviousData,
      // Matches the legacy useExtendInfiniteQuery default (gcTime ?? 0): don't
      // preserve page state across unmount, so returning to a deep page doesn't
      // replay N background fetches. Consumer queryCacheOptions.gcTime still
      // overrides this at the hook (spread after the factory).
      gcTime: 0,
    }),

  listPaginated: (
    dispatch: CreditCardQueriesDispatch,
    filters: GetCreditCardsRequest | undefined,
  ) =>
    queryOptions({
      queryKey: creditCardsQueryKeys.list(filters),
      queryFn: async () => {
        if (!filters) {
          throw new Error(
            'creditCardQueries.listPaginated: filters is required',
          )
        }
        dispatch({ type: GET_CREDIT_CARDS_INIT })
        try {
          const data = await getCreditCardsPaginated(filters)
          // TODO: lots of side-effects in creditCards.actions.tsx that need to be implemented for a full refactor
          dispatch({
            type: GET_CREDIT_CARDS_SUCCESS,
            payload: { ...data, queryParameters: filters },
          })
          return data
        } catch (error) {
          dispatch({
            type: GET_CREDIT_CARDS_FAILURE,
            payload: {
              ...(toReduxErrorPayload(error) as object),
              queryParameters: filters,
            },
          })
          throw error
        }
      },
      enabled: filters !== undefined,
      select: (data) => ({
        creditCards: data.creditCards.map(
          (creditCard) => new CreditCardListModel(creditCard),
        ),
        page: data.pagination.page,
        totalItems: data.pagination.totalItems,
      }),
    }),
}

interface QueryCreditCardOptions
  extends ExtendBaseQueryOptions<
    { creditCard: CreditCardListResponse },
    CreditCardListModel
  > {
  creditCardId: string | undefined
}

export const useQueryCreditCard = ({
  creditCardId,
  enabled = true,
  retry,
  ...rest
}: QueryCreditCardOptions) => {
  const dispatch = useDispatch<CreditCardQueriesDispatch>()
  return useQuery({
    ...creditCardQueries.detail(dispatch, creditCardId),
    // TODO: we currently rely on the 404 error for the use-case of a user not having permission
    // and this is an anti-pattern against how react query usually handles error states
    // but this imitates behaviour today of making a single fetch when the user opens the flyout
    retry,
    ...rest,
    // preserve the factory's arg-presence guard AND'd with any consumer enabled
    // (a spread override would otherwise replace it, not merge it)
    enabled: enabled && creditCardId !== undefined,
  })
}

interface QueryCreditCardPermissionsOptions
  extends ExtendBaseQueryOptions<
    CreditCardPermissionsResponse,
    CreditCardPermissionsModel
  > {
  creditCardId: string | undefined
}

export const useQueryCreditCardPermissions = ({
  creditCardId,
  enabled = true,
  retry,
  ...rest
}: QueryCreditCardPermissionsOptions) => {
  const dispatch = useDispatch<CreditCardQueriesDispatch>()
  return useQuery({
    ...creditCardQueries.permissions(dispatch, creditCardId),
    // TODO: we currently rely on the 404 error for the use-case of a user not having permission
    // and this is an anti-pattern against how react query usually handles error states
    // but this imitates behaviour today of making a single fetch when the user opens the flyout
    retry,
    ...rest,
    enabled: enabled && creditCardId !== undefined,
  })
}

interface QueryCreditCardsOptions
  extends ExtendBaseInfiniteQueryOptions<
    CreditCardsResponse,
    number,
    CreditCardsDataModel
  > {
  filters: GetCreditCardsRequest | undefined
  additionalKey?: string
}

export const useQueryCreditCards = ({
  filters,
  enabled = true,
  queryCacheOptions,
  additionalKey,
  ...rest
}: QueryCreditCardsOptions) => {
  const dispatch = useDispatch<CreditCardQueriesDispatch>()
  const infiniteQuery = useInfiniteQuery({
    ...creditCardQueries.list(dispatch, filters, additionalKey),
    ...queryCacheOptions,
    ...rest,
    enabled: enabled && filters !== undefined,
  })

  return {
    ...infiniteQuery,
    complete: !infiniteQuery.hasNextPage && !infiniteQuery.isFetching,
  }
}

interface QueryCreditCardsPaginatedOptions
  extends ExtendBaseQueryOptions<CreditCardsResponse, CreditCardsDataModel> {
  filters: GetCreditCardsRequest | undefined
}

export const useQueryCreditCardsPaginated = ({
  filters,
  enabled = true,
  queryCacheOptions,
  ...rest
}: QueryCreditCardsPaginatedOptions) => {
  const dispatch = useDispatch<CreditCardQueriesDispatch>()
  return useQuery({
    ...creditCardQueries.listPaginated(dispatch, filters),
    ...queryCacheOptions,
    ...rest,
    enabled: enabled && filters !== undefined,
  })
}

// `useVerifyCreditCard` and `useStartCreditCardVerification` migrated off the
// legacy `useExtendMutation` wrapper to the mutationOptions pattern in
// src/queries/creditCards/creditCardWrites.queries.tsx (EX-40100).
